import React, { useEffect, useState } from "react";
import { Plus, Trash2, Save, Loader2, MessageCircle } from "lucide-react";
import { adminApi } from "../../lib/api";
import { toast } from "sonner";

// Friendly labels for each editable prompt group (keys match ChatWidget's page matcher).
const PAGE_LABELS = {
  home: "Homepage",
  "legal-cashiering": "Legal Cashiering",
  pricing: "Pricing",
  services: "Services",
  about: "About",
  "meeting-room": "Meeting Room Hire",
  "business-health-check": "Business Growth Health Check",
  podcast: "Podcast",
  insights: "Blog / Insights",
  contact: "Contact",
};
const PAGE_ORDER = ["home", "legal-cashiering", "services", "pricing", "about", "meeting-room", "business-health-check", "podcast", "insights", "contact"];

function PromptList({ label, hint, items, onChange }) {
  const set = (i, v) => onChange(items.map((it, idx) => (idx === i ? v : it)));
  const add = () => onChange([...items, ""]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  return (
    <div className="rounded-xl border border-slate-200 p-4 bg-white">
      <div className="flex items-center justify-between mb-1">
        <h4 className="font-display font-bold text-navy text-sm">{label}</h4>
        <button type="button" onClick={add} data-testid={`cassie-add-${label}`} className="inline-flex items-center gap-1 text-xs font-semibold text-orange hover:text-orange-dark">
          <Plus size={14} /> Add question
        </button>
      </div>
      {hint && <p className="text-xs text-slate-400 mb-3">{hint}</p>}
      <div className="space-y-2">
        {items.length === 0 && <p className="text-xs text-slate-400 italic">No suggested questions — visitors won't see chips on these pages.</p>}
        {items.map((it, i) => (
          <div key={i} className="flex items-center gap-2">
            <input
              value={it}
              onChange={(e) => set(i, e.target.value)}
              placeholder="e.g. Do you work exclusively with InTouch?"
              className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange/40"
            />
            <button type="button" onClick={() => remove(i)} aria-label="Remove" className="text-slate-300 hover:text-red-500 shrink-0"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function CassiePromptsManager() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminApi.cassiePrompts.get().then((d) => setCfg({ heading: d.heading || "Popular questions", greeting: d.greeting || "", default: d.default || [], pages: d.pages || {} })).catch(() => toast.error("Couldn't load Cassie prompts"));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.cassiePrompts.update(cfg);
      toast.success("Saved — Cassie's suggested questions updated.");
    } catch {
      toast.error("Save failed. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) return <div className="p-6 text-slate-400 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Loading…</div>;

  const setPage = (key, items) => setCfg((c) => ({ ...c, pages: { ...c.pages, [key]: items } }));

  return (
    <div className="space-y-5" data-testid="cassie-prompts-manager">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display font-extrabold text-2xl text-navy flex items-center gap-2"><MessageCircle size={20} className="text-orange" /> Cassie Suggested Questions</h2>
          <p className="text-sm text-slate-500 mt-1">Tappable questions shown at the start of a chat, tailored to the page the visitor is on.</p>
        </div>
        <button onClick={save} disabled={saving} data-testid="cassie-prompts-save" className="inline-flex items-center gap-2 bg-orange hover:bg-orange-dark disabled:opacity-50 text-white font-bold text-sm px-6 py-3 rounded-full transition-colors shrink-0">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save changes
        </button>
      </div>

      <div className="rounded-xl border border-slate-200 p-4 bg-white space-y-4">
        <div>
          <label className="block text-sm font-semibold text-navy mb-1">Cassie's welcome message (intro)</label>
          <textarea
            value={cfg.greeting}
            onChange={(e) => setCfg((c) => ({ ...c, greeting: e.target.value }))}
            data-testid="cassie-prompts-greeting"
            rows={4}
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange/40"
          />
          <p className="text-xs text-slate-400 mt-1">The first message visitors see when they open the chat. Line breaks are kept.</p>
        </div>
        <div>
          <label className="block text-sm font-semibold text-navy mb-1">Heading above the questions</label>
          <input
            value={cfg.heading}
            onChange={(e) => setCfg((c) => ({ ...c, heading: e.target.value }))}
            data-testid="cassie-prompts-heading"
            className="w-full max-w-sm rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange/40"
          />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {PAGE_ORDER.map((key) => (
          <PromptList
            key={key}
            label={PAGE_LABELS[key]}
            items={cfg.pages[key] || []}
            onChange={(items) => setPage(key, items)}
          />
        ))}
        <PromptList
          label="Default (all other pages)"
          hint="Shown on any page not listed above."
          items={cfg.default}
          onChange={(items) => setCfg((c) => ({ ...c, default: items }))}
        />
      </div>

      <div className="flex justify-end">
        <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-orange hover:bg-orange-dark disabled:opacity-50 text-white font-bold text-sm px-6 py-3 rounded-full transition-colors">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save changes
        </button>
      </div>
    </div>
  );
}
